import i18n from "i18next";
import Router from "next/router";
import { useEffect, useRef, useState } from "react";
import {
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import { Trip } from "../types/Trip";
import { UserTypes } from "../types/User";
import k from "./../i18n/keys";

export function routerPushByUserType(type: string) {
  switch (type) {
    case UserTypes.USER:
      return Router.push("/app"); // Default route for users

    case UserTypes.AMEEGO:
      return Router.push("/ameego");

    case UserTypes.SERVICE_PROVIDER:
      return Router.push("/service");

    case UserTypes.ADMIN:
      return Router.push("/app/admin");

    default:
      return Router.push("/app"); // Fallback
  }
}

export function formatTripLocation(trip: Trip) {
  if (!trip.country) return;

  return trip.city
    ? trip.city.long_name + i18n.t(k._6) + " " + trip.country.long_name
    : trip.country.long_name;
}

export const useRefState = (initialValue = null) => {
  const [state, setState] = useState(initialValue);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  return [state, stateRef, setState];
};

/**
 * https://github.com/Minishlink/physbook/blob/02a0d5d7ca0d5d2cc6d308a3a9b81244c63b3f14/app/Resources/public/js/app.js#L177
 *
 * @param  {String} base64String
 * @return {Uint8Array}
 */
export const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export function getUserTypeDisplay(userType: UserTypes) {
  let userTypeDisplay;
  switch (userType) {
    case UserTypes.ADMIN:
      userTypeDisplay = "ADMINISTRATOR";
      break;
    case UserTypes.USER:
      userTypeDisplay = "Personal";
      break;
    case UserTypes.AMEEGO:
      userTypeDisplay = "Ameego";
      break;
    case UserTypes.SERVICE_PROVIDER:
      userTypeDisplay = "Business";
      break;
  }
  return userTypeDisplay;
}

export const scrollTo = (id) => {
  const element = document.getElementById(id);

  element.scrollIntoView({
    behavior: "smooth",
  });
};

export const convertHashtagsToLinks = (text) => {
  return text
    ? text.replace(
        /#(\w+)/g,
        "<a onClick=\"window.router.push('/app/search?keywords=%23$1')\">#$1</a>"
      )
    : "";
};

export const calculateRecommendationRating = (
  totalRecommendations: number,
  numPositiveRecommendations: number
): number => {
  let rating = (100 * numPositiveRecommendations) / totalRecommendations;
  if (rating < 1) {
    return 0;
  }

  if (rating >= 1 && rating <= 20) {
    return 1;
  }

  if (rating >= 21 && rating <= 40) {
    return 2;
  }

  if (rating >= 41 && rating <= 60) {
    return 3;
  }

  if (rating >= 61 && rating <= 80) {
    return 4;
  }

  if (rating >= 81) {
    return 5;
  }
};

export const isValidName = (name) => {
  if (name?.trim()?.length >= 2) {
    return true;
  } else {
    return false;
  }
};

export const isValidTelephone = (num) => {
  if (num?.trim()?.length >= 12) {
    return true;
  } else {
    return false;
  }
};

export const truncateName = (str, length) => {
  if (str.length <= length) {
    return str;
  }

  let subString = str.slice(0, length - 1); // Get max allowed string
  subString = subString.trim(); // Trim whitespace

  if (subString.slice(0, subString.lastIndexOf(" ") > 0))
    return subString.slice(0, subString.lastIndexOf(" ") + 2) + ". ";
  else return subString;
};

export const GetOnboardedStatus = async (me, setIsOnboarded) => {
  setIsOnboarded(me.stripe_onboarded);
};

export const ToTitleCase = (str: string): string => {
  return str.replace(/\b\w/g, function (txt) {
    return txt.toUpperCase();
  });
};

// e.g. boyne-valley to boyneValley
export const ConvertStringFromKebabCaseToCamelCase = (str: string): string => {
  return str.replace(/-([a-z])/g, (_, match) => match.toUpperCase());
};

// Boyne Valley to boyne-valley
export const ConvertStringFromTitleCaseToKebabCase = (str: string): string => {
  return (
    str
      // Convert spaces to dashes
      .replace(/\s+/g, "-")
      // Convert all characters to lowercase except the first character
      .toLowerCase()
  );
};

// e.g. boyneValley to Boyne Valley
export const ConvertStringFromCamelCaseToTitleCase = (str: string): string => {
  return (
    str
      // Insert a space before all caps
      .replace(/([A-Z])/g, " $1")
      // Capitalize the first character and remove dashes
      .replace(/^./, (match) => match.toUpperCase())
      // Capitalize the first character after a dash
      .replace(/-([a-z])/g, (_, match) => match.toUpperCase())
  );
};

// +3530868771234 -> +353868771234
export const removeLeadingLocalZeroFromTelephone = (tel) => {
  if (tel) {
    const phoneNumber = parsePhoneNumber(tel);
    if (phoneNumber) {
      const country = phoneNumber.country;
      if (country) {
        const countryCode = getCountryCallingCode(country);
        const stripSpaces = tel.replaceAll(" ", "");
        const telNoLeadingZero = stripSpaces.replace(
          "+" + countryCode + "0",
          "+" + countryCode
        );
        return telNoLeadingZero;
      }
    }
  }
  return tel;
};

// hello there -> hellothere, hello-there -> hellothere, hello There -> hellothere
// Useful for providing a hashtag to the feed
export const RemoveDashesToLowerCase = (input) => {
  return input.replace(/[\s-]/g, "").toLowerCase();
};

// Converts dashes to spaces and makes title case:
// hello-there -> Hello There
// hello there -> Hello There
// hello-there-world -> Hello There World
export const ReplaceDashWithSpacesToTitleCase = (input: string) => {
  return input
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// hello-there -> hello there
export const ConvertDashesToSpaces = (str: string = "") => {
  return str.replace(/-/g, " ");
};
